import React, { useMemo, useRef, useState, useEffect } from "react";
import { Modal, ModalDialog, Button, Form } from "react-bootstrap";
import Draggable from "react-draggable";
import { ImageCropper } from "./ImageCropper";
import Loader from "./Loader";
function CustomModalDialog(props) {
    return (
        <Draggable handle=".modal-header" cancel=".modal-body">
            <div>
                <ModalDialog
                    {...props}
                    style={{
                        maxWidth: "85vw",
                        width: "85vw",
                        maxHeight: "85vh"
                    }}
                />
            </div>
        </Draggable>
    );
}

const ScreenshotModal = ({ show, handleClose, rawSvgDataUrl }) => {
    const [invertColors, setInvertColors] = useState(false);
    const [transparentBg, setTransparentBg] = useState(false);
    const [removeGridLines, setRemoveGridLines] = useState(false);
    const [removeBorders, setRemoveBorders] = useState(false);
    const [whiteText, setWhiteText] = useState(false);
    const [svgText, setSvgText] = useState("");
    const [step, setStep] = useState(1);
    const [pngDataUrl, setPngDataUrl] = useState(null);
    const cropperRef = useRef(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        setInvertColors(false);
        setTransparentBg(false);
        setRemoveBorders(false);
        setRemoveGridLines(false);
        setWhiteText(false);
        setStep(1);
        if (!rawSvgDataUrl) {
            setSvgText("");
            setLoading(false);
            return;
        }
        fetch(rawSvgDataUrl)
            .then(res => res.text())
            .then(text => {
                setSvgText(text);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch SVG text:", err);
                setSvgText("");
                setLoading(false);
            });
    }, [rawSvgDataUrl]);

    const processedSvgText = useMemo(() => {
        if (!svgText) return "";
        let updatedSvgText = svgText;
        if (invertColors) {
            updatedSvgText = updatedSvgText.replace(/<svg([^>]*)>/, `<svg$1 style="filter: invert(1);">`);
        }
        if (transparentBg) {
            updatedSvgText = updatedSvgText.replace(/<rect[^>]*fill=['"](white|#fff|#ffffff)['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<rect[^>]*fill\s*=\s*['"](white|#fff|#ffffff)['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<rect[^>]*class\s*=\s*['"]highcharts-background['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<rect[^>]*class\s*=\s*['"]highcharts-legend-box['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/style\s*=\s*['"][^'"]*background[^'"]*['"]/gi, match => {
                return match.replace(/background[^;"]*;?/gi, "");
            });
        }
        if (removeGridLines) {
            updatedSvgText = updatedSvgText.replace(/<path[^>]*class=['"]highcharts-grid-line['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<line[^>]*class=['"]highcharts-tick['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<path[^>]*class=['"]highcharts-axis-line['"][^>]*>/gi, "");
            updatedSvgText = updatedSvgText.replace(/<path[^>]*class=['"]highcharts-minor-grid-line['"][^>]*>/gi, "");
        }
        if (removeBorders) {
            updatedSvgText = updatedSvgText.replace(/<rect[^>]*class\s*=\s*['"]highcharts-plot-border['"][^>]*>/gi, "");
        }
        if (whiteText) {
            updatedSvgText = updatedSvgText.replace(/<foreignObject([^>]*)>([\s\S]*?)<\/foreignObject>/gi, (match, attributes, htmlContent) => {
                const updatedHtmlContent = htmlContent.replace(/<(div|span|h1|h2|h3|h4|h5|h6|tspan|strong|p)([^>]*)>/gi, (htmlMatch, tag, htmlAttributes) => {
                    const hasStyle = /style\s*=\s*['"]/.test(htmlAttributes);
                    let styleAttr = "";
                    if (hasStyle) {
                        styleAttr = htmlAttributes.replace(/style\s*=\s*['"]([^'"]*)['"]/i, (styleMatch, styleContent) => {
                            let updatedStyle = styleContent.replace(/color\s*:\s*[^;"]*/gi, "color: white");

                            const isBold = /font-weight\s*:\s*(bold|700|800|900)/i.test(updatedStyle);
                            if (!isBold) {
                                updatedStyle += "; font-weight: bold";
                            }

                            if (!/white-space\s*:\s*nowrap/i.test(updatedStyle)) {
                                updatedStyle += "; white-space: nowrap";
                            }

                            return `style="${updatedStyle}"`;
                        });
                    } else {
                        styleAttr = ' style="color: white; font-weight: bold; white-space: nowrap;"';
                    }
                    return `<${tag}${styleAttr}>`;
                });

                return `<foreignObject${attributes}>${updatedHtmlContent}</foreignObject>`;
            });

            const whiteTextStyle = `
            <style>
                .highcharts-axis-labels text,
                .highcharts-data-label text,
                .highcharts-data-label tspan,
                .highcharts-title,
                .highcharts-subtitle,
                .highcharts-legend-item text,
                .highcharts-yaxis-labels text,
                .highcharts-xaxis-labels text,
                .highcharts-legend-title text,
                .highcharts-stack-labels text,
                .highcharts-axis-labels tspan,
                .highcharts-xaxis-labels tspan,
                .highcharts-yaxis-labels tspan,
                .highcharts-stack-labels tspan {
                    fill: white !important;
                    stroke: white !important;
                    stroke-width: 0px !important;
                    font-weight: bold !important;
                }
            </style>
            `;

            updatedSvgText = updatedSvgText.replace(/<svg([^>]*)>/, `<svg$1>${whiteTextStyle}`);
        }
        return updatedSvgText;
    }, [svgText, invertColors, transparentBg, removeGridLines, removeBorders, whiteText]);

    const convertSvgToPng = () => {
        setLoading(true);
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(processedSvgText, "image/svg+xml");
        const svgElement = svgDoc.documentElement;
        const width = parseInt(svgElement.getAttribute("width")) || 1000;
        const height = parseInt(svgElement.getAttribute("height")) || 1000;

        const img = new Image();
        img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(processedSvgText);
        img.onload = () => {
            const scaleFactor = 3;
            const canvas = document.createElement("canvas");
            canvas.width = width * scaleFactor;
            canvas.height = height * scaleFactor;
            const ctx = canvas.getContext("2d");

            ctx.imageSmoothingEnabled = false;
            ctx.scale(scaleFactor, scaleFactor);
            ctx.drawImage(img, 0, 0);

            setPngDataUrl(canvas.toDataURL("image/png", 1.0));
            setStep(2);
            setLoading(false);
        };
    };

    const handleDownload = format => {
        if (!cropperRef.current) return;
        const cropper = cropperRef.current.cropper;
        const canvas = cropper.getCroppedCanvas({ imageSmoothingQuality: "high" });
        if (canvas) {
            if (format === "jpg") {
                const newCanvas = document.createElement("canvas");
                newCanvas.width = canvas.width;
                newCanvas.height = canvas.height;
                const ctx = newCanvas.getContext("2d");
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                ctx.drawImage(canvas, 0, 0);
                const dataUrl = newCanvas.toDataURL("image/jpeg", 1.0);
                downloadImage(dataUrl, "FCA-Screenshot.jpg");
            } else {
                const dataUrl = canvas.toDataURL("image/png", 1.0);
                downloadImage(dataUrl, "FCA-Screenshot.png");
            }
        }
    };

    const downloadImage = (dataUrl, fileName) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const dynamicBackground = whiteText
        ? {
              backgroundColor: "#333",
              padding: "10px",
              maxWidth: "100%",
              maxHeight: "65vh",
              height: "65vh",
              overflow: "hidden"
          }
        : chessboardBackground;

    return (
        <Modal show={show} backdrop="static" onHide={handleClose} dialogAs={CustomModalDialog}>
            <Modal.Header closeButton className="draggable">
                <Modal.Title>{step === 1 ? "Customize Screenshot" : "Crop Screenshot"}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ textAlign: "center" }}>
                {loading ? (
                    <div style={dynamicBackground}>
                        <Loader />
                    </div>
                ) : step === 1 ? (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-evenly", alignItems: "center" }}>
                            <Form.Check
                                type="checkbox"
                                label="Make All Text White (for Dark Bg)"
                                checked={whiteText}
                                onChange={e => setWhiteText(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                label="Invert Colors"
                                checked={invertColors}
                                onChange={e => setInvertColors(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                label="Transparent Background"
                                checked={transparentBg}
                                onChange={e => setTransparentBg(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                label="Remove Chart Grid Lines"
                                checked={removeGridLines}
                                onChange={e => setRemoveGridLines(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                label="Remove Chart Borders"
                                checked={removeBorders}
                                onChange={e => setRemoveBorders(e.target.checked)}
                            />
                        </div>
                        <div style={dynamicBackground}>
                            <img
                                src={`data:image/svg+xml,${encodeURIComponent(processedSvgText)}`}
                                alt="SVG Preview"
                                style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain", border: "1px solid #ccc", background: "none" }}
                            />
                        </div>
                    </>
                ) : (
                    <div style={dynamicBackground}>
                        <ImageCropper src={pngDataUrl} cropperRef={cropperRef} />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                {step === 1 ? <Button onClick={convertSvgToPng}>Next</Button> : <Button onClick={() => setStep(1)}>Back</Button>}
                {step === 2 && (
                    <>
                        <Button variant="success" onClick={() => handleDownload("png")}>
                            Download PNG
                        </Button>
                        {!transparentBg && (
                            <Button variant="success" onClick={() => handleDownload("jpg")}>
                                Download JPG
                            </Button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

const chessboardBackground = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `
        linear-gradient(45deg, #ccc 25%, transparent 25%), 
        linear-gradient(-45deg, #ccc 25%, transparent 25%), 
        linear-gradient(45deg, transparent 75%, #ccc 75%), 
        linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
    backgroundSize: "16px 16px",
    backgroundPosition: "0 0, 8px 0, 8px -8px, 0px 8px",
    padding: "10px",
    maxWidth: "100%",
    maxHeight: "65vh",
    height: "65vh",
    overflow: "hidden"
};

export default ScreenshotModal;
